import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, LayoutTemplate, XIcon } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/library/dialog";

/**
 * Clicking "Report" in the sidebar opens the reports page and asks there which
 * kind of report to show (owner, 2026-10-19: "if clicked redirect to the
 * reports then ask the popup with background blur"): the ready-made widget
 * pages, or the custom reports (`/report?custom=1`, where the page's own
 * "Custom" button goes). The sidebar link carries a one-shot history-state
 * flag; the page reads it, asks once, and drops it — so Back, a reload, a
 * typed URL or a link straight to a report never ask.
 */

/** History-state key the sidebar sets on its Report link. */
const ASK_REPORT_KIND = "askReportKind";

/** The sidebar route that asks ("/report", "report", "/report/"). */
export function isReportNavPath(path: unknown): boolean {
  return typeof path === "string" && path.replace(/^\/+|\/+$/g, "").split("?")[0] === "report";
}

/** The state a sidebar link navigates with: the Report link also asks which kind. */
export function sidebarNavState(path: unknown, state: Record<string, unknown>): Record<string, unknown> {
  return isReportNavPath(path) ? { ...state, [ASK_REPORT_KIND]: true } : state;
}

type Kind = "standard" | "custom";

const OPTIONS: {
  kind: Kind;
  icon: typeof BarChart3;
  title: string;
  description: string;
}[] = [
  {
    kind: "standard",
    icon: BarChart3,
    title: "Standard reports",
    description: "Ready-made widgets: lead overview, sources, countries, stages, user performance, calls and branches.",
  },
  {
    kind: "custom",
    icon: LayoutTemplate,
    title: "Custom reports",
    description: "Reports built for your team, or a new one with your own rows, columns and filters.",
  },
];

/** A small drawing of what each kind looks like — bars for the widgets, a grid for the builder. */
function KindPreview({ kind }: { kind: Kind }) {
  if (kind === "standard") {
    return (
      <div className="flex h-14 items-end gap-1.5 rounded-lg bg-[#f2faf7] px-3 pb-2.5" aria-hidden="true">
        {[45, 70, 38, 88, 60, 76].map((h, i) => (
          <span
            key={i}
            className="w-full rounded-t-[3px] bg-[#218760] transition-opacity group-hover:opacity-100"
            style={{ height: `${h}%`, opacity: 0.35 + (i % 3) * 0.2 }}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="flex h-14 flex-col justify-center gap-1.5 rounded-lg bg-[#f2faf7] px-3" aria-hidden="true">
      {[0, 1, 2].map((r) => (
        <div key={r} className="flex items-center gap-1.5">
          <span className={`h-1.5 w-8 rounded-full ${r === 0 ? "bg-[#156548]/70" : "bg-[#218760]/30"}`} />
          <span className="h-1.5 flex-1 rounded-full bg-[#218760]/15" />
          <span className={`h-1.5 w-5 rounded-full ${r === 0 ? "bg-[#156548]/70" : "bg-[#218760]/45"}`} />
        </div>
      ))}
    </div>
  );
}

/**
 * Mounted on the reports page. Open while the current history entry carries
 * the sidebar's flag; any answer (or closing it) replaces the entry without
 * the flag. Closing keeps the standard reports, which are already on screen.
 */
export default function ReportKindPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as Record<string, unknown> | null;
  const asking = state?.[ASK_REPORT_KIND] === true;

  const answer = (kind: Kind | null) => {
    const rest = { ...(state ?? {}) };
    delete rest[ASK_REPORT_KIND];
    const search = new URLSearchParams(location.search);
    if (kind === "custom") search.set("custom", "1");
    const qs = search.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : "" },
      { replace: true, state: Object.keys(rest).length ? rest : null },
    );
  };

  return (
    <Dialog open={asking} onOpenChange={(open) => !open && answer(null)}>
      <DialogPortal>
        <DialogOverlay className="bg-[#051912]/25 backdrop-blur-[6px]" />
        <DialogPrimitive.Content
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[540px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 duration-200 outline-none"
        >
          <div className="flex items-start gap-3 border-b border-[#e8efec] bg-gradient-to-b from-[#f2faf7] to-white px-6 pt-5 pb-4">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[#218760]/[0.12] text-[#156548]">
              <BarChart3 className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[17px] font-medium tracking-[-0.34px] text-[#051912]">
                Open reports
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] text-[#697571]">
                Which reports do you want to see?
              </DialogDescription>
            </div>
            <DialogClose
              className="grid size-8 shrink-0 place-items-center rounded-full text-[#8A9692] transition hover:bg-[#eef3f1] hover:text-[#051912] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#218760]"
              aria-label="Close and stay on the standard reports"
            >
              <XIcon className="size-4" />
            </DialogClose>
          </div>

          <div className="grid gap-3 p-5 sm:grid-cols-2">
            {OPTIONS.map((o, i) => (
              <button
                key={o.kind}
                type="button"
                autoFocus={i === 0}
                onClick={() => answer(o.kind)}
                className="group flex flex-col gap-3 rounded-xl border border-[#e3ece8] bg-white p-3.5 text-left transition-[border-color,background-color,box-shadow] hover:border-[#218760]/45 hover:bg-[#f9fcfb] hover:shadow-[0_4px_14px_rgba(21,101,72,0.08)] focus-visible:border-[#218760] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#218760]/20"
              >
                <KindPreview kind={o.kind} />
                <div className="flex items-center gap-2">
                  <o.icon className="size-4 shrink-0 text-[#156548]" />
                  <span className="text-[14px] font-medium tracking-[-0.2px] text-[#051912]">{o.title}</span>
                </div>
                <span className="-mt-1.5 text-[12.5px] leading-[1.45] text-[#697571]">{o.description}</span>
                <span className="mt-auto inline-flex items-center gap-1 text-[12.5px] font-medium text-[#218760]">
                  Open
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
